/**
 * Types for in-text citation linking: finding author-year citation spans in plain
 * text and resolving each cited work against a CSL reference list on evidence alone.
 *
 * Everything here is plain data. There is no editor, DOM or I/O involvement, so the
 * same code runs in a browser and in Node.
 */

/** One name on a CSL reference (the subset of CSL-JSON `name-variable` this module reads). */
export interface CslName {
  family?: string;
  given?: string;
  /** Some bibliography parsers put the surname prefix here (`{ family: "Meer", particle: "Van der" }`). */
  particle?: string;
  'non-dropping-particle'?: string;
  'dropping-particle'?: string;
  /** Corporate or otherwise unsplit names (`"World Health Organization"`). */
  literal?: string;
}

/** A CSL-JSON reference, restricted to the fields the linker reads. Extra fields are ignored. */
export interface CslRef {
  id?: string | number;
  author?: CslName[];
  /** Read when `author` is empty (an edited volume cited by its editors). */
  editor?: CslName[];
  issued?:
    | { 'date-parts'?: Array<Array<number | string>>; literal?: string; raw?: string }
    | string;
  /** A disambiguating letter (`"a"`), when the bibliography carries one. */
  'year-suffix'?: string;
  /** The bibliography line the reference was parsed from, when known. */
  rawReference?: string;
  /** Alias of `rawReference` used by some producers. */
  sourceText?: string;
  [k: string]: unknown;
}

/** How a normalized token relates to a reference; the weight of that evidence differs by kind. */
export type EvidenceKind =
  | 'family'
  | 'literal'
  | 'particle-family'
  | 'given-family'
  | 'hyphen-part'
  | 'raw-head';

/** The normalized evidence one reference offers, built once by `indexReferences`. */
export interface ReferenceEvidence {
  id: string;
  /** Forms the FIRST author may be written as in a citation → the kind of evidence each form is. */
  firstAuthor: Map<string, EvidenceKind>;
  /** Forms ANY author (first included) may be written as → kind. */
  authors: Map<string, EvidenceKind>;
  /** Normalized given names and initials of every author. Indexed for completeness; the
   *  span grammar does not capture initials, so the resolver does not score on these. */
  given: Set<string>;
  /** Normalized tokens of the raw bibliography line up to (not including) its year. */
  rawHead: string[];
  /** Four-digit year as a string, or null when the reference carries no usable year. */
  year: string | null;
  /** `"a"`, `"b"`, … or the empty string. */
  yearSuffix: string;
  /** Number of authors on the reference; 0 when unknown. */
  authorCount: number;
}

export interface ReferenceIndex {
  /** Evidence per reference id. */
  refs: Map<string, ReferenceEvidence>;
  /** `<normalized first-author form>|<year>` → every reference id claiming that key. A key
   *  with more than one id is a family/year collision the citation must disambiguate. */
  byFamilyYear: Map<string, Set<string>>;
}

/** One cited work inside a citation span: the names as written plus one year, or one
 *  reference number. */
export interface Constituent {
  /** Offsets into the text handed to `findCitationSpans`, `[start, end)`: from the first name
   *  (or, for a further year in a run, from that year) to the end of the year or its locator. */
  start: number;
  end: number;
  text: string;
  /** Name groups exactly as written, in order (`["Van der Meer", "Cho"]`). A bare-year
   *  continuation (`; 2023`) carries the names of the constituent before it. */
  names: string[];
  /** Offset in the text of each entry of `names`. For a bare-year continuation these lie
   *  before `start`, inside the constituent that first wrote the names. */
  nameOffsets: number[];
  /** Lower-case surname particles found inside `names` (`["van", "der"]`). */
  particles: string[];
  year: string;
  yearSuffix: string;
  etAl: boolean;
  /** Number of name groups written out. */
  arity: number;
  /** The reference number a numeric citation names (`[3]` → 3). Set only on the constituents
   *  of a `numeric` span, whose name fields are empty. */
  ordinal?: number;
  /** Text inside a parenthetical that sits before the names and is not part of them —
   *  `see`, `e.g.,`, `cf.`, `vgl.`, free prose such as `for a review, see`, and any words the
   *  resolver trimmed off the first name. Replacing a whole parenthetical must carry it. */
  prefix?: string;
  locator?: string;
}

export interface CitationSpan {
  start: number;
  end: number;
  text: string;
  /** `parenthetical` and `narrative` are author-year forms; `numeric` is `[1]`, `(1)` or `¹`. */
  kind: 'parenthetical' | 'narrative' | 'numeric';
  constituents: Constituent[];
}

/**
 * Set on a resolved or ambiguous result that the resolver reached only after trimming leading
 * material no reference claims off the written names (`Curiously, Smith and Jones` →
 * `Smith and Jones`; `Results of Smith` → `Smith`). Absent when the names resolved as written.
 */
export interface Trim {
  /** Index into `Constituent.names` of the first name that was used. */
  namesFrom: number;
  /** The exact text dropped from the start of the constituent (`Curiously, `, `Results of `);
   *  empty when the names lie outside the constituent's own text (a bare-year continuation). */
  trimmedPrefix: string;
}

export type Resolution =
  | ({ kind: 'resolved'; id: string; score: number; evidence: string[] } & Partial<Trim>)
  | ({ kind: 'ambiguous'; candidates: string[]; reason: string } & Partial<Trim>)
  | {
      kind: 'unresolved';
      reason: string;
      /** References whose written first author and co-authors match, but which carry no year: the
       *  work may be cited here, and the bibliography does not say. Never linked on the name alone. */
      undated?: string[];
    };

export interface LinkedItem {
  constituent: Constituent;
  resolution: Resolution;
}

export interface LinkedSpan {
  span: CitationSpan;
  /** `linked` only when EVERY constituent resolved; otherwise `partial`, and the caller must
   *  not link anything in the span. */
  kind: 'linked' | 'partial';
  items: LinkedItem[];
}
